import { useState, useRef, useEffect } from 'react';
import { User, LogOut, ChevronDown, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserRole } from '../lib/api';

interface ProfileDropdownProps {
  /** `pen` = Start Wave avatar for Penalty App; default keeps Timing Dashboard look */
  variant?: 'default' | 'pen';
}

export function ProfileDropdown({ variant = 'default' }: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Fetch user role when component mounts
  useEffect(() => {
    async function fetchUserRole() {
      if (!user?.email) return;

      try {
        const response = await getUserRole(user.email);
        
        if (response.success && response.data) {
          setUserRole(response.data.rolename);
        } else {
          console.log('Failed to fetch user role:', response.error);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    }

    fetchUserRole();
  }, [user?.email]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('stationSetupComplete');
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleChangePassword = () => {
    setIsOpen(false);
    navigate('/change-password');
  };

  if (!user) return null;

  const initials =
    (user.email?.slice(0, 2) || 'U').toUpperCase();

  if (variant === 'pen') {
    return (
      <div className="relative font-brutal" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-8 h-8 rounded-full grid place-items-center text-[11px] font-black border-2 transition-colors ${
            isOpen
              ? 'bg-sw-nav-2 border-sw-yellow text-sw-yellow'
              : 'bg-sw-nav-2 border-sw-line text-white hover:border-sw-yellow hover:text-sw-yellow'
          }`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          title={user.email || 'Profile'}
        >
          {initials}
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-52 bg-sw-panel border border-sw-border rounded-sm shadow-lg py-2 z-50">
            <div className="px-4 py-3 border-b border-sw-border">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-sw-muted">
                Signed in as
              </p>
              <p className="text-sm text-white truncate mt-1">{user.email}</p>
              {userRole && (
                <p className="text-xs text-sw-yellow mt-1 font-extrabold uppercase tracking-wide">
                  {userRole}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleChangePassword}
              className="w-full flex items-center px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-white hover:bg-black hover:text-sw-yellow transition-colors"
            >
              <KeyRound className="w-4 h-4 mr-3" />
              Change password
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 text-xs font-extrabold uppercase tracking-wide text-white hover:bg-black hover:text-sw-yellow transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign out
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 ${
          isOpen ? 'bg-[#676660] text-white' : 'hover:bg-gray-100'
        }`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isOpen ? 'bg-[#676660]' : 'bg-white'
        }`}>
          <User className={`w-5 h-5 ${
            isOpen ? 'text-white' : 'text-gray-900'
          }`} />
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
          isOpen ? 'rotate-180 text-white' : 'text-white'
        }`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-[#676660] rounded-lg shadow-lg border border-gray-700 py-2 z-50">
          <div className="px-4 py-3 border-b border-gray-600">
            <p className="text-sm font-medium text-white">Signed in as</p>
            <p className="text-sm text-gray-200 truncate">{user.email}</p>
            {userRole && (
              <p className="text-sm text-white mt-1 font-medium">{userRole}</p>
            )}
          </div>

          <button
            type="button"
            onClick={handleChangePassword}
            className="w-full flex items-center px-4 py-3 text-sm text-white hover:bg-gray-600 transition-colors duration-200"
          >
            <KeyRound className="w-4 h-4 mr-3 text-white" />
            Change password
          </button>
          
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 text-sm text-white hover:bg-gray-600 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4 mr-3 text-white" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
